import { FC, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../app/hooks'
import { selectIsQueueEnabled } from '../application/applicationSlice'
import {
    clearJumpToSeekbarPosition,
    handleAudioComplete,
    selectAllArticles,
    selectAudioSpeed,
    selectAudioState,
    selectCurrentlyPlayingArticle,
    selectJumpToSeekbarPosition,
    selectVolume,
    setArticleOnAudioComplete,
    setAudioDuration,
    setAudioPlayerProgressed,
} from '../article/data/articleSlice'
import { useQueue } from '../queue/hooks/useQueue'
import { selectArticleToPlayAfterQueue, selectQueuedArticles, selectSuggestedArticles } from '../queue/queueSlice'

interface IProps {}

export const AudioPlayer: FC<IProps> = () => {
    const dispatch = useDispatch()

    const audioRef: React.RefObject<HTMLAudioElement> = useRef(null)

    const currentlyPlayingArticle = useAppSelector(selectCurrentlyPlayingArticle)
    const audioState = useAppSelector(selectAudioState)
    const jumpToSeekbarPosition = useAppSelector(selectJumpToSeekbarPosition)
    const audioSpeed = useAppSelector(selectAudioSpeed)
    const articles = useAppSelector(selectAllArticles)
    const queuedArticles = useAppSelector(selectQueuedArticles)
    const suggestedArticles = useAppSelector(selectSuggestedArticles)
    const articleToPlayAfterQueue = useAppSelector(selectArticleToPlayAfterQueue)
    const volume = useAppSelector(selectVolume)
    const isQueueEnabled = useAppSelector(selectIsQueueEnabled)

    const { updateArticleToPlayAfterQueue } = useQueue()

    const onTimeUpdate = () => {
        if (audioRef && audioRef.current) {
            dispatch(setAudioPlayerProgressed(audioRef.current.currentTime))
        }
    }

    const onDurationUpdated = () => {
        if (audioRef && audioRef.current) {
            dispatch(setAudioDuration(audioRef.current.duration))
        }
    }

    const maybeUpdateSpeed = () => {
        if (audioRef && audioRef.current && audioRef.current.playbackRate !== audioSpeed) {
            audioRef.current.playbackRate = audioSpeed
        }
    }

    const onAudioEnd = () => {
        if (currentlyPlayingArticle != null) {
            if ((queuedArticles.length || suggestedArticles.length) && isQueueEnabled) {
                if (!currentlyPlayingArticle.queueId) {
                    // play this article once all the queued articles are played
                    updateArticleToPlayAfterQueue(currentlyPlayingArticle)
                }

                if (queuedArticles.length) {
                    dispatch(setArticleOnAudioComplete(queuedArticles[0]))
                } else {
                    dispatch(setArticleOnAudioComplete(suggestedArticles[0]))
                }
            }
            // if it's last queue article then continue with the normal articles from the last checkpoint
            else if (currentlyPlayingArticle.queueId && isQueueEnabled) {
                if (articleToPlayAfterQueue) {
                    dispatch(setArticleOnAudioComplete(articleToPlayAfterQueue))
                } else {
                    dispatch(setArticleOnAudioComplete(articles[0]))
                }
            } else {
                dispatch(handleAudioComplete(currentlyPlayingArticle))
            }
        }
    }

    const maybeUpdatePlayingState = () => {
        // HTMLMediaElement.HAVE_CURRENT_DATA means audio is loaded
        if (audioRef && audioRef.current && audioRef.current.readyState >= HTMLMediaElement.HAVE_METADATA) {
            if (audioState === 'playing') {
                if (audioRef.current.paused) {
                    audioRef.current.play()
                }
            } else if (audioState === 'paused') {
                if (!audioRef.current.paused) {
                    audioRef.current.pause()
                }
            }
        }
    }

    useEffect(() => {
        if (
            audioRef &&
            audioRef.current &&
            jumpToSeekbarPosition != null &&
            jumpToSeekbarPosition !== audioRef.current.currentTime
        ) {
            audioRef.current.currentTime = jumpToSeekbarPosition
            dispatch(clearJumpToSeekbarPosition())
        }
    }, [jumpToSeekbarPosition])

    useEffect(() => {
        if (audioRef && audioRef.current) {
            audioRef.current.volume = volume
        }
    }, [volume])

    maybeUpdatePlayingState()
    maybeUpdateSpeed()

    return (
        <audio
            ref={audioRef}
            src={currentlyPlayingArticle ? currentlyPlayingArticle.audio : undefined}
            onLoad={maybeUpdatePlayingState}
            onTimeUpdate={onTimeUpdate}
            onDurationChange={onDurationUpdated}
            onEnded={onAudioEnd}
            autoPlay={audioState === 'playing'}>
            Audio is not supported on this browser.
        </audio>
    )
}
